import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import s from './ui.module.css';

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  icon?: boolean;
  wide?: boolean;
}

export function Button({ variant = 'secondary', icon, wide, className, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      className={cx(s.button, s[variant], icon && s.iconButton, wide && s.wide, className)}
      {...rest}
    />
  );
}

/** Linkkien ja painikkeiden yhteinen ulkoasu, kun elementti on <a> tai <Link>. */
export function buttonClass(variant: 'primary' | 'secondary' | 'ghost' = 'secondary'): string {
  return cx(s.button, s[variant]);
}

// ---------------------------------------------------------------------------
// Lomakekentät
// ---------------------------------------------------------------------------

interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string;
  children: (id: string, describedBy: string | undefined) => ReactNode;
}

function FieldShell({ label, hint, error, children }: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={s.field}>
      <label htmlFor={id} className={s.label}>
        {label}
      </label>
      {children(id, describedBy)}
      {hint && (
        <p id={hintId} className={s.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={s.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextField({ label, hint, error, className, ...rest }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      {(id, describedBy) => (
        <input
          id={id}
          className={cx(s.input, className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextArea({ label, hint, error, ...rest }: TextAreaProps) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      {(id, describedBy) => (
        <textarea
          id={id}
          className={s.input}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
}

export function SelectField({ label, hint, children, ...rest }: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint}>
      {(id, describedBy) => (
        <select id={id} className={s.input} aria-describedby={describedBy} {...rest}>
          {children}
        </select>
      )}
    </FieldShell>
  );
}

// ---------------------------------------------------------------------------
// Valintakortit
// ---------------------------------------------------------------------------

export interface ChoiceOption<T extends string> {
  value: T;
  title: string;
  text?: string;
  disabled?: boolean;
}

interface ChoiceGroupProps<T extends string> {
  name: string;
  legend: string;
  /** null = ei vielä valittu (käyttäjän on valittava itse). */
  value: T | null;
  options: ChoiceOption<T>[];
  onChange: (value: T) => void;
}

export function ChoiceGroup<T extends string>({
  name,
  legend,
  value,
  options,
  onChange,
}: ChoiceGroupProps<T>) {
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className={s.label} style={{ marginBottom: '0.5rem' }}>
        {legend}
      </legend>
      <div className={s.choices}>
        {options.map((option) => (
          <label
            key={option.value}
            className={cx(
              s.choice,
              option.value === value && s.choiceSelected,
              option.disabled && s.choiceDisabled,
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === value}
              disabled={option.disabled}
              onChange={() => onChange(option.value)}
            />
            <span className={s.choiceTitle}>{option.title}</span>
            {option.text && <span className={s.choiceText}>{option.text}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Osion valitsin
// ---------------------------------------------------------------------------

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

/**
 * Painikerivi, josta valitaan näkyvä osa (esim. Laskut / Kulut). Valinta näkyy
 * aria-pressed-tilana; puhelimessa rivi vierii sivuttain.
 */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
}) {
  return (
    <div className={s.segmented} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={cx(s.segment, option.value === value && s.segmentActive)}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.count !== undefined && <span className={s.segmentCount}>{option.count}</span>}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paneelit ja tilat
// ---------------------------------------------------------------------------

export function Panel({
  children,
  padded = true,
  className,
}: {
  children: ReactNode;
  padded?: boolean;
  className?: string;
}) {
  return <section className={cx(s.panel, padded && s.panelPadded, className)}>{children}</section>;
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  text?: string;
  action?: ReactNode;
}

export function EmptyState({ icon, title, text, action }: EmptyStateProps) {
  return (
    <div className={s.empty}>
      {icon && <div className={s.emptyIcon}>{icon}</div>}
      <h2 className={s.emptyTitle}>{title}</h2>
      {text && <p className={s.emptyText}>{text}</p>}
      {action}
    </div>
  );
}

/** Rakentamaton osio: kertoo lyhyesti, mitä tähän tulee. */
export function ComingSoon({ title, text }: { title?: string; text: string }) {
  const { t } = useTranslation();
  return (
    <div className={s.empty}>
      <span className={s.soonBadge}>{t('common.comingSoon')}</span>
      {title && <h2 className={s.emptyTitle}>{title}</h2>}
      <p className={s.emptyText}>{text}</p>
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div role="status" aria-live="polite">
      <div className={s.spinner} />
      {label && <span className="visually-hidden">{label}</span>}
    </div>
  );
}

export function Splash() {
  const { t } = useTranslation();
  return (
    <div className={s.splash}>
      <Spinner label={t('common.loading')} />
    </div>
  );
}

export function Alert({ children }: { children: ReactNode }) {
  return (
    <p className={s.alert} role="alert">
      {children}
    </p>
  );
}

export function Notice({ children }: { children: ReactNode }) {
  return <p className={s.notice}>{children}</p>;
}

// ---------------------------------------------------------------------------
// Dialogi (oma toteutus: <dialog> puuttuu Safari 14:stä)
// ---------------------------------------------------------------------------

interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Leveämpi dialogi lomakkeille, joissa on kenttiä rinnakkain. */
  wide?: boolean;
}

export function Dialog({ open, title, onClose, children, wide }: DialogProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>(
      'input, textarea, select, button:not([data-close])',
    );
    first?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;
  // Portaali bodyyn: muuten esim. yläpalkin backdrop-filter rajaisi position: fixed -taustan
  // yläpalkin kokoiseksi, ja dialogista näkyisi vain osa.
  return createPortal(
    <div className={s.overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={ref}
        className={cx(s.dialog, wide && s.dialogWide)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className={s.dialogHead}>
          <h2 id={titleId} className={s.dialogTitle}>
            {title}
          </h2>
          <Button variant="ghost" icon onClick={onClose} aria-label={t('common.close')} data-close>
            <X size={18} />
          </Button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function DialogActions({ children }: { children: ReactNode }) {
  return <div className={s.dialogActions}>{children}</div>;
}

export function DialogSubhead({ children }: { children: ReactNode }) {
  return <h3 className={s.dialogSubhead}>{children}</h3>;
}
